import React, { useState } from 'react';
import { Badge, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { MdClose, MdContentPasteSearch } from 'react-icons/md';
import moment from 'moment-timezone';
import reportService from '../../../services/reportService';
import WinLoseVoucherDialog from './WinLoseVoucherDialog';

const toNumber = (value) => Number(value) || 0;

const formatAmount = (value) =>
    toNumber(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getSection = (items = [], key) => items.find((item) => item[key])?.[key] || {};

const getFirstAvailableSection = (items = []) => {
    for (const key of ['total', 'single', 'mix']) {
        const section = getSection(items, key);

        if (Object.keys(section).length) {
            return section;
        }
    }

    return {};
};

const getValue = (row = {}, keys = []) => {
    for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
            return row[key];
        }
    }

    return '';
};

const getDetailsRows = (user = {}) => {
    const details = user.details || user.detail || user.vouchers || user.voucherDetails || user.records || user.children;

    return Array.isArray(details) && details.length ? details : [];
};

const normalizeVoucherResponse = (response = {}) => {
    const data = response.data ?? response;

    if (data?.viewDetails) {
        return data;
    }

    if (data?.data?.viewDetails) {
        return data.data;
    }

    if (Array.isArray(data?.data)) {
        return { viewDetails: data.data };
    }

    if (Array.isArray(data)) {
        return { viewDetails: data };
    }

    return data?.data || data || {};
};

const getProfitSection = (row = {}, groupKey, fallbackKeys = []) => {
    const group = row[groupKey];

    if (Array.isArray(group)) {
        return getFirstAvailableSection(group);
    }

    if (group && typeof group === 'object') {
        return group;
    }

    for (const key of fallbackKeys) {
        const fallback = row[key];

        if (Array.isArray(fallback)) {
            return getFirstAvailableSection(fallback);
        }

        if (fallback && typeof fallback === 'object') {
            return fallback;
        }
    }

    return {};
};

const ProfitCell = ({ value, highlight = false }) => (
    <TableCell
        align="right"
        className={highlight ? 'bg-yellow-300 dark:bg-customAltas/80' : ''}
        sx={{ border: '1px solid #111' }}
    >
        <span className={toNumber(value) < 0 ? 'text-red-500' : 'text-black dark:text-customWhite'}>
            {formatAmount(value)}
        </span>
    </TableCell>
);

const UserDetailsPanel = ({ user, loading = false, onClose, startDate, endDate }) => {
    const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
    const [voucherLoading, setVoucherLoading] = useState(false);
    const [voucherError, setVoucherError] = useState('');
    const [voucherData, setVoucherData] = useState({});
    const [selectedVoucherRow, setSelectedVoucherRow] = useState(null);

    if (!user) {
        return null;
    }

    const detailsRows = getDetailsRows(user);

    const loadVoucherDetails = async (row) => {
        const voucher = getValue(row, ['voucherNo', 'postingNo', 'voucher', 'voucherId', 'id']);
        const gamblingId = getValue(row, ['gamblingId', 'gamblingID', 'id']);
        // const payload = {
        //     ...(gamblingId ? { gamblingId } : {}),
        //     ...(voucher ? { voucherNo: voucher, postingNo: voucher } : {}),
        //     ...(user.id ? { userId: user.id } : {}),
        //     ...(startDate ? { startDate } : {}),
        //     ...(endDate ? { endDate } : {}),
        // };
        const payload = {
            gamblingWinId : row?.gamblingWinId || row?.gamblingId || row?.gamblingID || row?.id,
        }

        setSelectedVoucherRow(row);
        setVoucherDialogOpen(true);
        setVoucherLoading(true);
        setVoucherError('');
        setVoucherData({ voucherNo: voucher });

        try {
            const response = await reportService.winloseReportVoucher(payload);
            const normalizedData = normalizeVoucherResponse(response);

            setVoucherData({
                voucherNo: voucher,
                ...normalizedData,
            });
        } catch (error) {
            console.error('Failed to fetch voucher details:', error);
            setVoucherError('Unable to load voucher details.');
            setVoucherData({ voucherNo: voucher });
        } finally {
            setVoucherLoading(false);
        }
    };

    return (
        <div className="mt-3">
            <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-base font-semibold text-background dark:text-customWhite">user details</p>
                <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-background hover:bg-gray-100 dark:border-custom_dark_primary2 dark:text-customWhite dark:hover:bg-custom_dark_primary2"
                    onClick={onClose}
                    aria-label="Close user details"
                    title="Close"
                >
                    <MdClose className="text-lg" />
                </button>
            </div>
            <TableContainer component={Paper} className="shadow-md rounded-xl overflow-x-auto">
                <Table size="small" aria-label="user details table">
                    <TableHead>
                        <TableRow className="bg-gray-300 dark:bg-custom_dark_primary2">
                            <TableCell sx={{ border: '1px solid #111' }}>
                                <span className="font-semibold dark:text-customWhite">Date & Voucher</span>
                            </TableCell>
                            <TableCell sx={{ border: '1px solid #111' }}>
                                <span className="font-semibold dark:text-customWhite">Event</span>
                            </TableCell>
                            {['W/L', 'Com', 'W/L+Com', 'W/L', 'Com', 'W/L+Com', 'W/L', 'Com', 'W/L+Com'].map((label, index) => (
                                <TableCell key={`${label}-${index}`} align="right" sx={{ border: '1px solid #111' }}>
                                    <span className="font-semibold dark:text-customWhite">{label}</span>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow className="dark:bg-custom_dark_background">
                                <TableCell colSpan={11} align="center" sx={{ border: '1px solid #111' }}>
                                    <span className="dark:text-customWhite">Loading user details...</span>
                                </TableCell>
                            </TableRow>
                        ) : user.error ? (
                            <TableRow className="dark:bg-custom_dark_background">
                                <TableCell colSpan={11} align="center" sx={{ border: '1px solid #111' }}>
                                    <span className="text-red-500">{user.error}</span>
                                </TableCell>
                            </TableRow>
                        ) : detailsRows.length ? (
                            detailsRows.map((row, index) => {
                                const member = getProfitSection(row, 'member', ['memberProfit']);
                                const master = getProfitSection(row, 'master', ['admin', 'masterProfit', 'adminProfit']);
                                const company = getProfitSection(row, 'company', ['companyProfit']);
                                const voucher = getValue(row, ['voucherNo', 'postingNo', 'voucher', 'voucherId', 'id']);
                                const date = getValue(row, ['createdDate', 'date', 'createdAt', 'selectedDate']);
                                const event = getValue(row, ['eventName', 'event', 'match', 'leagueName', 'rapidEventId']);

                                return (
                                    <TableRow key={`${voucher || event || 'detail'}-${index}`} className="dark:bg-custom_dark_background">
                                        <TableCell sx={{ border: '1px solid #111' }}>
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <span className="dark:text-customWhite">{voucher || '-'}</span>
                                                    {row.gamblingTypeId === 2 && (
                                                        <div className={`px-1 rounded text-[10px] bg-customFontVoilet text-white dark:bg-blue-800 dark:text-blue-100`}>
                                                            {'mix'}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-[10px] text-blue">{moment(date).format('YYYY-MM-DD hh:mm A') || '-'}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell sx={{ border: '1px solid #111' }}>
                                            {/* <span className="dark:text-customWhite">{event || '-'}</span> */}
                                            {/* <button type="button" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                                                Event
                                            </button> */}
                                            <button
                                                type="button"
                                                onClick={() => loadVoucherDetails(row)}
                                                disabled={voucherLoading}
                                                className="inline-flex items-center gap-2 rounded-md border border-customAccentBlue/40 bg-customAccentBlue px-4 py-[5px] text-sm text-white transition hover:bg-customAccentBlue/80 focus:outline-none focus:ring-2 focus:ring-customAccentBlue/30 disabled:cursor-not-allowed disabled:opacity-60 dark:border-customAccent/50 dark:bg-customAccent dark:text-customHeading dark:hover:bg-customAccent/80"
                                            >
                                                <MdContentPasteSearch className="text-base" />
                                                View
                                            </button>
                                        </TableCell>
                                        <ProfitCell value={member.wl ?? row.memberWL ?? row.memberWinLose ?? row.subWl} />
                                        <ProfitCell value={member.com ?? row.memberCom ?? row.subCom} />
                                        <ProfitCell value={member.total ?? row.memberTotal ?? row.memberWLCom ?? row.subWlPm} />
                                        <ProfitCell value={master.wl ?? row.masterWL ?? row.adminWL ?? row.userWl} highlight />
                                        <ProfitCell value={master.com ?? row.masterCom ?? row.adminCom ?? row.userCom} highlight />
                                        <ProfitCell value={master.total ?? row.masterTotal ?? row.adminTotal ?? row.userWlPm} highlight />
                                        <ProfitCell value={company.wl ?? row.companyWL ?? row.upWl} />
                                        <ProfitCell value={company.com ?? row.companyCom ?? row.upCom} />
                                        <ProfitCell value={company.total ?? row.companyTotal ?? row.companyWLCom ?? row.upWlPm} />
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow className="dark:bg-custom_dark_background">
                                <TableCell colSpan={11} align="center" sx={{ border: '1px solid #111' }}>
                                    <span className="dark:text-customWhite">No user details found.</span>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <WinLoseVoucherDialog
                open={voucherDialogOpen}
                voucher={voucherData}
                loading={voucherLoading}
                error={voucherError}
                onClose={() => setVoucherDialogOpen(false)}
                onRetry={() => selectedVoucherRow && loadVoucherDetails(selectedVoucherRow)}
            />
        </div>
    );
};

export default UserDetailsPanel;
